import { FormattedMessage } from 'react-intl';
import { LegalNotice } from '../../../../utils/checkoutState';
import { config, getAssetUrl } from '../../../../utils/config';
import { useModal } from '../../../../utils/modal';
import useIsMobileDevice from '../../../../utils/useIsMobileDevice';
import Button from '../../../atoms/Button';
import Paragraph from '../../../atoms/Paragraph';
import SubHeadline from '../../../atoms/SubHeadline';
import './style.scss';

type LegalDetailsProps = {
  details: LegalNotice;
  onClose: () => void;
};

const LegalDetails = ({ details, onClose }: LegalDetailsProps) => {
  const isMobile = useIsMobileDevice();
  const { legalName, streetWithNumber, zip, city, country, telephone, email, vatId, managingDirectors } = details;
  const { registryCourtCity, parentCompanyLegalName, parentCompanyRegistryCourtCity } = details;
  const { parentCompanyRegisterNumber, representative, registerNumber, shippingMerchantName } = details;
  const { parentCompanyRegistryCourtType, registryCourtType } = details;

  return (
    <div className="merchant-legal-details" data-test-id="merchant-legal-details">
      {isMobile && (
        <img
          src={getAssetUrl('header/chevronRight.svg')}
          className="merchant-legal-details__arrow-icon"
          alt="down"
          onClick={() => onClose()}
        ></img>
      )}
      <div className="merchant-legal-details__scrollable">
        <h3 className="merchant-legal-details__title" data-test-id="merchant-legal-details-title">
          <FormattedMessage id={'basket.merchant.title'} />
        </h3>
        {legalName && (
          <div className="merchant-legal-details__section" data-test-id="merchant-legal-name-section">
            {config.basket.showMerchantInformation && (
              <SubHeadline className="merchant-legal-details__section__title" data-test-id="merchant-legal-name-title">
                <FormattedMessage
                  id={`basket.merchant.${legalName === shippingMerchantName ? 'soldAndShippedBy' : 'soldBy'}`}
                  values={{ legalName }}
                />
              </SubHeadline>
            )}
            {streetWithNumber && (
              <Paragraph
                className="merchant-legal-details__section__paragraph"
                data-test-id="merchant-street-with-number"
              >
                {streetWithNumber}
              </Paragraph>
            )}
            {zip && city && (
              <Paragraph className="merchant-legal-details__section__paragraph" data-test-id="merchant-zip-city">
                {zip} {city}
              </Paragraph>
            )}
            {country && (
              <Paragraph className="merchant-legal-details__section__paragraph" data-test-id="merchant-country">
                {country?.iso3166Alpha2Code}
              </Paragraph>
            )}
          </div>
        )}
        {telephone && (
          <div className="merchant-legal-details__section" data-test-id="merchant-telephone-section">
            <SubHeadline className="merchant-legal-details__section__subtitle">
              <FormattedMessage id={'basket.merchant.phoneNumber'} />
            </SubHeadline>
            <Paragraph className="merchant-legal-details__section__paragraph" data-test-id="merchant-telephone">
              {telephone}
            </Paragraph>
          </div>
        )}
        {email && (
          <div className="merchant-legal-details__section" data-test-id="merchant-email-section">
            <SubHeadline className="merchant-legal-details__section__subtitle">
              <FormattedMessage id={'basket.merchant.email'} />
            </SubHeadline>
            <Paragraph className="merchant-legal-details__section__paragraph" data-test-id="merchant-email">
              {email}
            </Paragraph>
          </div>
        )}
        {vatId && (
          <div className="merchant-legal-details__section" data-test-id="merchant-vat-id-section">
            <SubHeadline className="merchant-legal-details__section__subtitle">
              <FormattedMessage id={'basket.merchant.taxId'} />
            </SubHeadline>
            <Paragraph className="merchant-legal-details__section__paragraph" data-test-id="merchant-vat-id">
              {vatId}
            </Paragraph>
            {registryCourtType && registryCourtCity && (
              <Paragraph className="merchant-legal-details__section__paragraph" data-test-id="merchant-registry-court">
                {registryCourtType?.[config.locale] ?? registryCourtType?.[config.locale.split('_')[0]]}{' '}
                {registryCourtCity}
              </Paragraph>
            )}
            {registerNumber && (
              <Paragraph className="merchant-legal-details__section__paragraph" data-test-id="merchant-register-number">
                {registerNumber}
              </Paragraph>
            )}
          </div>
        )}
        {parentCompanyLegalName && (
          <div className="merchant-legal-details__section" data-test-id="merchant-parent-company-legal-name-section">
            <SubHeadline className="merchant-legal-details__section__subtitle">
              <FormattedMessage id={'basket.merchant.legalRepresentation'} />
            </SubHeadline>
            <Paragraph
              className="merchant-legal-details__section__paragraph"
              data-test-id="merchant-parent-company-legal-name"
            >
              {parentCompanyLegalName}
            </Paragraph>
            {parentCompanyRegistryCourtType && parentCompanyRegistryCourtCity && (
              <Paragraph
                className="merchant-legal-details__section__paragraph"
                data-test-id="merchant-parent-company-registry-court"
              >
                {parentCompanyRegistryCourtType?.[config.locale] ??
                  parentCompanyRegistryCourtType?.[config.locale.split('_')[0]]}{' '}
                {parentCompanyRegistryCourtCity}
              </Paragraph>
            )}
            {parentCompanyRegisterNumber && (
              <Paragraph
                className="merchant-legal-details__section__paragraph"
                data-test-id="merchant-parent-company-register-number"
              >
                {parentCompanyRegisterNumber}
              </Paragraph>
            )}
          </div>
        )}
        {managingDirectors && (
          <div className="merchant-legal-details__section" data-test-id="merchant-managing-directors-section">
            <SubHeadline className="merchant-legal-details__section__subtitle">
              <FormattedMessage id={'basket.merchant.representation'} />
            </SubHeadline>
            {managingDirectors?.split(',').map(item => (
              <Paragraph
                className="merchant-legal-details__section__paragraph"
                data-test-id="merchant-managing-director"
                key={item}
              >
                {item}
              </Paragraph>
            ))}
          </div>
        )}
        {representative && (
          <div className="merchant-legal-details__section" data-test-id="merchant-representative-section">
            <SubHeadline className="merchant-legal-details__section__subtitle">
              <FormattedMessage id={'basket.merchant.supervisoryBoard'} />
            </SubHeadline>
            {representative?.split(',').map(item => (
              <Paragraph
                className="merchant-legal-details__section__paragraph"
                data-test-id="merchant-representative"
                key={item}
              >
                {item}
              </Paragraph>
            ))}
          </div>
        )}
        {!Object.keys(details).length && (
          <div className="merchant-legal-details__section" data-test-id="merchant-no-data-section">
            <Paragraph className="merchant-legal-details__section__paragraph" data-test-id="merchant-no-data">
              <FormattedMessage id={'basket.merchant.noMerchantData'} />
            </Paragraph>
          </div>
        )}
      </div>
      <div className="merchant-legal-details__button">
        <Button secondary type="button" data-test-id="merchant-close-btn" onClick={() => onClose()}>
          <FormattedMessage id="generic.close" />
        </Button>
      </div>
    </div>
  );
};

const MerchantDetails = ({ details }: { details: LegalNotice }) => {
  const { toggleModal } = useModal();

  const merchantModalConfig = {
    children: <LegalDetails details={details} onClose={toggleModal} />,
    onEscapeKey: () => toggleModal(),
    onClose: () => toggleModal(),
    contentClassName: 'merchant-details__modal',
    backgroundClassName: 'merchant-details__modal-background',
    onClickOutside: () => toggleModal()
  };

  return (
    <div className="merchant-details" data-test-id="item-merchant-details">
      <FormattedMessage id={'basket.merchant.soldBy'} values={{ legalName: details.legalName }} />
      <img
        className="merchant-details__icon"
        src={getAssetUrl('common/info.svg')}
        onClick={() => toggleModal(merchantModalConfig)}
        data-test-id="item-merchant-details-icon"
      />
    </div>
  );
};

export default MerchantDetails;
